import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Circle, Target, Undo2, Zap } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { useOnboarding } from './OnboardingContext';
import MilestoneBanner from './MilestoneBanner';
import ShareSavingsDialog from '../ShareSavingsDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/tax-snapshot/savings-progress`;
const money = (n) => `$${Math.round(n || 0).toLocaleString()}`;

const MarkDonePopover = ({ strategy, onSave, testPrefix = 'tracker', className = '' }) => {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const save = async () => {
    const value = amount === '' ? null : Number(amount);
    if (value !== null && (Number.isNaN(value) || value < 0)) return toast.error('Enter a valid amount');
    await onSave(strategy.id, value);
    setOpen(false);
    setAmount('');
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className={`rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50 ${className}`} data-testid={`${testPrefix}-mark-${strategy.id}`}>
          I did this
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72" data-testid={`${testPrefix}-popover-${strategy.id}`}>
        <p className="text-sm font-medium text-stone-900">How much did this save you?</p>
        <p className="mt-1 text-xs text-stone-500">Optional. Leave blank to use the estimated midpoint of {strategy.display}.</p>
        <div className="mt-3 flex gap-2">
          <Input type="number" min="0" inputMode="decimal" placeholder="e.g. 1200" value={amount}
            onChange={(e) => setAmount(e.target.value)} data-testid={`${testPrefix}-amount-${strategy.id}`} />
          <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`${testPrefix}-save-${strategy.id}`}>Save</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const TrackerRow = ({ s, onSave, onUndo }) => {
  const done = s.captured > 0;
  return (
    <li className="flex items-center gap-4 py-3.5 border-b border-stone-100 last:border-0" data-testid={`tracker-row-${s.id}`}>
      {done ? <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" /> : <Circle className="h-5 w-5 shrink-0 text-stone-300" />}
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-medium ${done ? 'text-stone-900' : 'text-stone-700'}`}>{s.title}</p>
        <p className="mt-0.5 flex items-center gap-1.5 text-xs text-stone-500" data-testid={`tracker-detail-${s.id}`}>
          {s.source === 'auto' && <Zap className="h-3 w-3 text-amber-500" />}
          {s.detail || `Potential ${s.display}`}
        </p>
      </div>
      {done && <span className="font-mono text-sm tabular-nums text-emerald-700" data-testid={`tracker-captured-${s.id}`}>+{money(s.captured)}</span>}
      {s.marked_done ? (
        <Button size="icon" variant="ghost" onClick={() => onUndo(s.id)} aria-label={`Undo ${s.title}`} className="h-8 w-8 text-stone-400 hover:text-stone-700" data-testid={`tracker-undo-${s.id}`}>
          <Undo2 className="h-4 w-4" />
        </Button>
      ) : s.source !== 'auto' && <MarkDonePopover strategy={s} onSave={onSave} />}
    </li>
  );
};

export const SavingsProgressTracker = () => {
  const { onboardingData } = useOnboarding();
  const progress = onboardingData?.progress;
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    axios.get(API, { withCredentials: true }).then(r => setData(r.data)).catch(() => setData(null));
  }, []);

  useEffect(() => { if (progress?.profile_completed) load(); }, [progress?.profile_completed, progress?.updated_at, load]);

  const onSave = async (id, amount) => {
    try {
      const r = await axios.post(`${API}/${id}`, { amount }, { withCredentials: true });
      setData(r.data);
      toast.success('Nice work — savings captured');
    } catch { toast.error('Could not save. Please try again.'); }
  };
  const ackMilestone = () => {
    const m = data?.milestone?.percent;
    if (!m) return;
    setData(d => ({ ...d, milestone: null }));
    axios.post(`${API}/milestones/${m}/ack`, {}, { withCredentials: true }).catch(() => {});
  };
  const onUndo = async (id) => {
    try {
      const r = await axios.delete(`${API}/${id}`, { withCredentials: true });
      setData(r.data);
    } catch { toast.error('Could not undo. Please try again.'); }
  };

  if (!progress?.profile_completed || !data || data.strategies.length === 0) return null;

  return (
    <section className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="savings-progress-tracker">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-emerald-700"><Target className="h-3.5 w-3.5" /> Savings tracker{data.mode === 'personalized' ? ` · ${data.year} tax year` : ''}</p>
          <h2 className="mt-1 text-base md:text-lg font-semibold text-stone-900">
            <span className="font-mono tabular-nums" data-testid="tracker-captured-total">{money(data.captured_total)}</span> captured
            <span className="font-normal text-stone-500"> of {money(data.potential_low)} – {money(data.potential_high)} potential</span>
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <p className="text-sm text-stone-500" data-testid="tracker-completed-count">{data.completed_count} of {data.strategies.length} strategies in action</p>
          <ShareSavingsDialog capturedTotal={data.captured_total} triggerClassName="border-stone-300" testId="tracker-share-btn" />
        </div>
      </div>
      <div className="mt-4 h-2 overflow-hidden rounded-full bg-stone-100" role="progressbar" aria-valuenow={data.percent} aria-valuemin={0} aria-valuemax={100} data-testid="tracker-progress-bar">
        <div className="h-full rounded-full bg-emerald-500 transition-[width] duration-700 ease-out" style={{ width: `${data.percent}%` }} />
      </div>
      {data.milestone && (
        <MilestoneBanner
          milestone={data.milestone}
          capturedTotal={data.captured_total}
          nextStrategy={data.next_strategy}
          onDismiss={ackMilestone}
          renderMarkButton={(s) => (
            <MarkDonePopover strategy={s} testPrefix="milestone" className="bg-transparent border-emerald-400 text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200"
              onSave={async (id, amount) => { ackMilestone(); await onSave(id, amount); }} />
          )}
        />
      )}
      <ul className="mt-3">
        {data.strategies.map(s => <TrackerRow key={s.id} s={s} onSave={onSave} onUndo={onUndo} />)}
      </ul>
      {data.mode === 'example' && (
        <p className="mt-3 text-xs text-stone-500" data-testid="tracker-example-note">Potential is based on an example profit — add your income to personalize it.</p>
      )}
    </section>
  );
};

export default SavingsProgressTracker;
